<template>
	<Col class="height-100 flex-justify-space-between overflow-x-hidden">
		<Col class="px-3 inner-content-container overflow-x-hidden overflow-y-auto">
			<Col class="px-2">
				<ShareFeatureDialogInnerTitle class="mb-3" text="Share with teammates" />
				<slot name="user-select-combobox" />
				<ShareDefaultPersonalVisibility
					v-if="showDefaultPersonalVisibility"
					class="mb-5"
					v-on="$listeners"
					:isLocked.sync="isLockedLocal"
					:isVisible.sync="isVisibleLocal"
					:showEditFeatureDescriptionLink="hasFeatureDescriptionOnCreated"
				/>
				<h2 v-if="showBeforeShareRecommendations && filteredRecommendationItems?.length > 0" class="heavy mb-2">Before you share</h2>
			</Col>
			<template v-if="showBeforeShareRecommendations">
				<BeforeShareRecommendationItem
					class="mt-3"
					:key="recommendationItem.title"
					:title="recommendationItem.title"
					@click.native="recommendationItem.onClick"
					:isRequired="recommendationItem.isRequired"
					:isCompleted="recommendationItem.isCompleted"
					v-for="recommendationItem in filteredRecommendationItems"
					:class="{ disabled: recommendationItem.isCompleted && recommendationItem.isRequired }"
				/>
			</template>
		</Col>
		<BeforeShareFooter
			v-on="$listeners"
			:isLoading="isLoadingShare"
			class="footer-container px-5 mt-5"
			:notifyByMail.sync="notifyByMailLocal"
			:isShareButtonDisabled="isShareButtonDisabled"
		/>
	</Col>
</template>
<script>
import BeforeShareFooter from '@/components/dialogs/ShareFeatureDialog/BeforeShareRecommendations/BeforeShareFooter.vue';
import BeforeShareRecommendationItem from '@/components/dialogs/ShareFeatureDialog/BeforeShareRecommendations/BeforeShareRecommendationItem.vue';
import ShareDefaultPersonalVisibility from '@/components/dialogs/ShareFeatureDialog/ShareDefaultPersonalVisibility.vue';
import ShareFeatureDialogInnerTitle from '@/components/dialogs/ShareFeatureDialog/ShareFeatureDialogInnerTitle.vue';
import { getCredentialsSuggestions as useCredentialsSuggestions } from '@/store/modules/credentials/getters';
import { isOnboardingEnabled } from '@/store/modules/featureFlags/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';

export default {
	name: 'BeforeShareRecommendationsMainContent',
	components: {
		BeforeShareFooter,
		ShareFeatureDialogInnerTitle,
		BeforeShareRecommendationItem,
		ShareDefaultPersonalVisibility,
	},
	props: {
		workflow: { type: Object },
		isLocked: { type: Boolean },
		isVisible: { type: Boolean },
		notifyByMail: { type: Boolean },
		isLoadingShare: { type: Boolean },
		hasFeatureDescription: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ isOnboardingEnabled, useCredentialsSuggestions }),
		showDefaultPersonalVisibility() {
			return false; // for now, this is disabled
			// return this.isAddOn;
		},
		isAddOn() {
			return WorkflowService.isOfTypeFeature(this.workflow);
		},
		nodesCredentialsSuggestions() {
			return this.useCredentialsSuggestions(this.workflow.id);
		},
		regularNodesSuggestions() {
			return this.nodesCredentialsSuggestions?.move_to_user_configuration || [];
		},
		interfaceNodesSuggestions() {
			return this.nodesCredentialsSuggestions?.search_fields_to_move_to_user_configuration || {};
		},
		hasCredentialsSuggestions() {
			return this.regularNodesSuggestions.length > 0 || Object.keys(this.interfaceNodesSuggestions).length > 0;
		},
		isShareButtonDisabled() {
			return this.hasCredentialsSuggestions;
		},
		showBeforeShareRecommendations() {
			return this.hasCredentialsSuggestionsOnCreated || !this.hasFeatureDescriptionOnCreated;
		},
		allPossibleRecommendationItems() {
			return [
				{
					isRequired: true,
					title: 'Reassign connections',
					isCompleted: !this.hasCredentialsSuggestions,
					show: this.hasCredentialsSuggestionsOnCreated,
					onClick: () => this.$emit('reassign-connections'),
				},
				{
					title: 'Add feature description',
					isCompleted: this.hasFeatureDescription,
					onClick: () => this.$emit('edit-feature-description'),
					show: !this.hasFeatureDescriptionOnCreated && this.isOnboardingEnabled,
				},
			];
		},
		filteredRecommendationItems() {
			return this.allPossibleRecommendationItems.filter((item) => item.show);
		},
		isLockedLocal: {
			get() {
				return this.isLocked;
			},
			set(value) {
				this.$emit('update:isLocked', value);
			},
		},
		isVisibleLocal: {
			get() {
				return this.isVisible;
			},
			set(value) {
				this.$emit('update:isVisible', value);
			},
		},
		notifyByMailLocal: {
			get() {
				return this.notifyByMail;
			},
			set(value) {
				this.$emit('update:notifyByMail', value);
			},
		},
	},
	methods: {
		initialize() {
			if (this.isOnboardingEnabled) {
				this.hasFeatureDescriptionOnCreated = this.hasFeatureDescription;
			}
			this.hasCredentialsSuggestionsOnCreated = this.hasCredentialsSuggestions;
		},
	},
	created() {
		this.initialize();
	},
	data() {
		return {
			hasFeatureDescriptionOnCreated: false,
			hasCredentialsSuggestionsOnCreated: false,
		};
	},
};
</script>
<style scoped lang="scss">
$footer-height: 80px;
.inner-content-container {
	height: calc(100% - #{$footer-height});
}
.footer-container {
	min-height: $footer-height;
}
.empty-moon {
	width: 25px;
	height: 25px;
	border-radius: 50%;
	background: #f4f6f9;
	border: 1px solid #d6ddea;
}
.disabled {
	opacity: 0.7;
	pointer-events: none;
}
</style>
